<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'EntityEvents',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    entityId: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const selectedEventId = ref(null);
  const eventData = ref(null);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noHeader: false,
    noPrimaryButton: true,
    size: 'full-screen',
  }));
  const b_7789867021953041_showColumnPicker = computed(() => false);
  const b_7789867021953041_headerShowReloadButton = computed(() => false);
  const b_7789867021953041_columns = computed(() => [
    {
      field: 'event_name',
      name: $fn.value.pgettext('EntityEvents', 'Event'),
      type: 'command',
    },
    {
      field: 'timestamp',
      name: $fn.value.pgettext('EntityEvents', 'Timestamp'),
      formatter: {
        name: 'datetime',
      },
    },
    {
      field: 'identity',
      name: $fn.value.pgettext('EntityEvents', 'User'),
      formatter: {
        name: 'user',
      },
    },
  ]);
  const b_2070266589801014_options = computed(() => ({
    lineNumbers: true,
    matchBrackets: true,
    continueComments: 'Enter',
  }));

  const $data = computed(() => ({
    selectedEventId: selectedEventId.value,
    eventData: eventData.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_7789867021953041_showColumnPicker:
      b_7789867021953041_showColumnPicker.value,
    b_7789867021953041_headerShowReloadButton:
      b_7789867021953041_headerShowReloadButton.value,
    b_7789867021953041_columns: b_7789867021953041_columns.value,
    b_2070266589801014_options: b_2070266589801014_options.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('list_entity_events', {
      parameters: { id: props.entityId },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedEventId' },
      { name: 'eventData' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      {
        name: 'b_7789867021953041_showColumnPicker',
        nodeId: '7789867021953041',
      },
      {
        name: 'b_7789867021953041_headerShowReloadButton',
        nodeId: '7789867021953041',
      },
      { name: 'b_7789867021953041_columns', nodeId: '7789867021953041' },
      { name: 'b_2070266589801014_options', nodeId: '2070266589801014' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedEventId,
      eventData,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_7789867021953041_showColumnPicker,
      b_7789867021953041_headerShowReloadButton,
      b_7789867021953041_columns,
      b_2070266589801014_options,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('EntityEvents/header', 'Entity events'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.entityId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('entityId');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_eik1uqhbv_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_7789867021953041_setData_dt0qy88f1_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedEventId',
      value: $event.data.data.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_7789867021953041_command_qvyl5mtu6_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'event_name';
  };
  const act_7789867021953041_setData_0t35qgujo_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'eventData',
      value: JSON.stringify($event.data.response.data, null, 2),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7789867021953041_request_ini4m6ydt_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'read_entity_event_data',
      data: { id: props.entityId, event_id: selectedEventId.value },
    };
  };
  const inst_evh_7789867021953041_command_o2jlqtp1b_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'event_name';
  };

  const act_7315092382398562_closeModal_eik1uqhbv =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_eik1uqhbv_getActionArgs,
    });
  const inst_evh_7315092382398562_close_cr0oyycgh =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_eik1uqhbv,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_cr0oyycgh],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_tyk8nxbx9 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_svl76ndde =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_tyk8nxbx9,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_svl76ndde],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_ww34n7j41 =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_3t1b41rx0 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_ww34n7j41,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_3t1b41rx0],
    event: 'close',
  });
  const act_7789867021953041_setData_dt0qy88f1 = new actionTypes.SetDataAction({
    actionArgs: act_7789867021953041_setData_dt0qy88f1_getActionArgs,
  });
  const inst_evh_7789867021953041_command_qvyl5mtu6 =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_setData_dt0qy88f1,
      event: 'command',
      when: inst_evh_7789867021953041_command_qvyl5mtu6_getWhen,
      displayName: 'setData',
    });
  const act_7789867021953041_setData_0t35qgujo = new actionTypes.SetDataAction({
    actionArgs: act_7789867021953041_setData_0t35qgujo_getActionArgs,
  });
  const inst_evh_7789867021953041_success_ocj25egv2 =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_setData_0t35qgujo,
      event: 'success',
      displayName: 'setData',
    });
  const act_7789867021953041_request_ini4m6ydt = new actionTypes.RequestAction({
    actionArgs: act_7789867021953041_request_ini4m6ydt_getActionArgs,
    displayName: 'loadEventData',
    events: [inst_evh_7789867021953041_success_ocj25egv2],
  });
  const inst_evh_7789867021953041_command_o2jlqtp1b =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_request_ini4m6ydt,
      event: 'command',
      when: inst_evh_7789867021953041_command_o2jlqtp1b_getWhen,
      displayName: 'loadEventData',
    });
  const inst_evh_7789867021953041_command = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_7789867021953041_command_qvyl5mtu6,
      inst_evh_7789867021953041_command_o2jlqtp1b,
    ],
    event: 'command',
  });

  const actions = {
    inst_evh_7315092382398562_close_cr0oyycgh,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_svl76ndde,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_3t1b41rx0,
    inst_evh_8564662037462133_close,
    inst_evh_7789867021953041_command_qvyl5mtu6,
    inst_evh_7789867021953041_success_ocj25egv2,
    inst_evh_7789867021953041_command_o2jlqtp1b,
    loadEventData: inst_evh_7789867021953041_command_o2jlqtp1b,
    inst_evh_7789867021953041_command,
  };

  const evh_7315092382398562_close_cr0oyycgh = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_cr0oyycgh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_svl76ndde = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_svl76ndde.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_3t1b41rx0 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_3t1b41rx0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_command_qvyl5mtu6 = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_command_qvyl5mtu6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_success_ocj25egv2 = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_success_ocj25egv2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_command_o2jlqtp1b = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_command_o2jlqtp1b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_command = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="EntityEvents"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" class="h-100" v-if="loaded">
      <div class="row h-100">
        <div class="col-6 h-100">
          <ItpDataGrid
            name="events"
            :headerTitle="pgettext('EntityEvents', 'Events')"
            :items="ds"
            checkbox-row-selection
            :showColumnPicker="b_7789867021953041_showColumnPicker"
            :headerShowReloadButton="b_7789867021953041_headerShowReloadButton"
            export-to-csv
            :exportToCsvFilename="pgettext('ExportFilename', 'payments')"
            :columns="b_7789867021953041_columns"
            @command="evh_7789867021953041_command($event, {})"
          >
          </ItpDataGrid>
        </div>
        <div class="col-6 h-100 overflow-hidden">
          <ItpCodeMirror
            mode="json"
            :options="b_2070266589801014_options"
            v-model="eventData"
            class="h-100 v-100"
          >
          </ItpCodeMirror>
        </div>
      </div>
    </UcBody>
  </ItpPage>
</template>
